import React from "react";
import PropTypes from "prop-types";
import { prProfileUrl, companyUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import { usePeerLastMessageSentAt } from "../../source_directory/LastMessagesContext/LastMessagesContext";
import formatLastMessageDate from "../../source_directory/formatLastMessageDate";
import SourceAvatar from "../../source_directory/SourceAvatar";
import { SourceBadgeList } from "../../source_directory/SourceBadges";
import HashtagPills from "../../source_directory/HashtagPills";
import HighlightAttribute from "../../source_directory/HighlightAttribute";
import isOwnPrProfile from "./viewerGating";
import { prCardRepresentsHref } from "../show/pr_sections";

function PrCardGrid({ hit }) {
  const isSignedIn = !!globalThis.Qwoted?.CURRENT_USER_ID;
  const canMessage = isSignedIn && !isOwnPrProfile(hit.id);
  const lastMessageDate = formatLastMessageDate(usePeerLastMessageSentAt(hit.id));
  const hashtags = hit.hashtags || [];
  const represents = [
    hit.experts_count > 0 && { count: hit.experts_count, label: hit.experts_count === 1 ? "Expert" : "Experts" },
    hit.products_count > 0 && { count: hit.products_count, label: hit.products_count === 1 ? "Product" : "Products" },
  ].filter(Boolean);

  return (
    <div
      className="animated faster fadeIn"
      style={{
        gridRow: "span 4",
        display: "grid",
        gridTemplateRows: "subgrid",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Row 1: Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 16px 14px" }}>
        <SourceAvatar
          imageUrl={hit.profile_image_url}
          name={hit.name}
          initialsText={hit.initials}
          size={60}
          fontSize={18}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 19, lineHeight: 1.25, letterSpacing: "-0.19px" }}>
            <a href={prProfileUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "#111827" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {hit.current_position_title || hit.current_position_workplace ? (
            <div style={{ fontSize: 14, color: "#6b7280", marginTop: 2 }}>
              <HighlightAttribute hit={hit} attribute="current_position_title" fallback={hit.current_position_title} />
              {hit.current_position_workplace && (
                <>
                  {hit.current_position_title ? " at " : ""}
                  {hit.current_position_workplace_slug ? (
                    <a
                      href={companyUrl({
                        slug: hit.current_position_workplace_slug,
                        migrate_id: hit.current_position_workplace_migrate_id,
                      })}
                      style={{ color: "#4d5357", textDecoration: "underline" }}
                    >
                      <HighlightAttribute
                        hit={hit}
                        attribute="current_position_workplace"
                        fallback={hit.current_position_workplace}
                      />
                    </a>
                  ) : (
                    <HighlightAttribute
                      hit={hit}
                      attribute="current_position_workplace"
                      fallback={hit.current_position_workplace}
                    />
                  )}
                </>
              )}
            </div>
          ) : null}
          <SourceBadgeList types={[hit.qwoted100 && "qwoted100", hit.quick_to_respond && "fast_to_respond"]} />
        </div>
        <SavedGroupsBookmark
          sourceableType="Id::Profile::Promoter"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>

      {/* Row 2: Separator — full-width (no padding; overflow:hidden on card clips to border-radius) */}
      <div style={{ height: 1, backgroundColor: "#e9e9e9" }} />

      {/* Row 3: Content */}
      <div style={{ padding: "8px 16px 0" }}>
        {hit.location_string && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#212528", marginBottom: 10 }}
          >
            <LocationMarkerIcon size={14} />
            {hit.location_string}
          </div>
        )}

        {hashtags.length > 0 && (
          <div>
            <div style={{ fontSize: 13, color: "#4d5357" }}>Covers:</div>
            <div style={{ marginTop: 4 }}>
              <HashtagPills hashtags={hashtags} max={4} />
            </div>
          </div>
        )}

        {represents.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 13, color: "#4d5357" }}>Represents:</div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, marginTop: 4 }}>
              {represents.map((r) => (
                <a
                  key={r.label}
                  href={prCardRepresentsHref(hit)}
                  style={{ fontSize: 14, color: "#212528", textDecoration: "underline" }}
                >
                  <strong>{r.count}</strong> {r.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Row 4: CTA — always rendered to maintain 4-child count for subgrid */}
      {canMessage ? (
        <div style={{ padding: "12px 16px 16px" }}>
          <MessageButton
            profileId={hit.id}
            buttonLabel="Start a Conversation"
            extraButtonClasses="w-100 mb-0"
            buttonStyle={{ fontSize: 14 }}
            openInboxThread
          />
          {lastMessageDate && (
            <div style={{ textAlign: "center", fontSize: 12, lineHeight: "17.4px", color: "#4d5357", marginTop: 10 }}>
              Last message sent {lastMessageDate}
            </div>
          )}
        </div>
      ) : (
        <div />
      )}
    </div>
  );
}

PrCardGrid.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string.isRequired,
    name: PropTypes.string,
    profile_image_url: PropTypes.string,
    initials: PropTypes.string,
    current_position_title: PropTypes.string,
    current_position_workplace: PropTypes.string,
    current_position_workplace_slug: PropTypes.string,
    location_string: PropTypes.string,
    hashtags: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          slug: PropTypes.string,
          hashtag_name: PropTypes.string,
          hashtagName: PropTypes.string,
        }),
      ]),
    ),
    experts_count: PropTypes.number,
    products_count: PropTypes.number,
    qwoted100: PropTypes.bool,
    quick_to_respond: PropTypes.bool,
  }).isRequired,
};

export default PrCardGrid;
